import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Video, RefreshCw, CheckCircle2, XCircle, Loader2, Zap, Link as LinkIcon, Settings, Copy, ExternalLink } from "lucide-react";

const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso || "—"; } };

/**
 * AdminVirtualMeetings — admin-side authorisation hub for Zoho Meetings.
 *   • Shows Zoho Meeting connection status + Self-Client code exchange form
 *   • Lists pending / authorised / rejected meeting requests
 *   • One-click Authorise → creates a real Zoho session and emails the partner
 *   • Quick-Start (admin composes a meeting for any enlisted partner)
 */
export function AdminVirtualMeetings({ api }) {
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState("");
  const [qs, setQs] = useState({ partner_email: "", topic: "", agenda: "", duration_mins: 30 });
  const [qsBusy, setQsBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/zoho/meeting/status"),
        api.get(`/admin/meeting-requests?status=${filter}`),
      ]);
      setStatus(a.data || null);
      setRows(b.data?.requests || []);
    } catch { toast.error("Could not load virtual meetings"); }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!code.trim()) return;
    setConnecting(true);
    try {
      await api.post("/admin/zoho/meeting/connect", { code: code.trim() });
      toast.success("Connecting to Zoho Meeting in the background…");
      setCode("");
      // Poll status until the background task lands on a terminal state.
      const started = Date.now();
      const interval = setInterval(async () => {
        try {
          const s = await api.get("/admin/zoho/meeting/status");
          setStatus(s.data);
          if (s.data?.state === "connected") {
            clearInterval(interval); setConnecting(false);
            toast.success(`Zoho Meeting connected · zsoid ${s.data.zsoid}`);
            load();
          } else if (s.data?.state === "failed") {
            clearInterval(interval); setConnecting(false);
            const err = s.data.last_error || {};
            toast.error(err.message || "Zoho Meeting connection failed");
          } else if (Date.now() - started > 90_000) {
            clearInterval(interval); setConnecting(false);
            toast.error("Still connecting… check the status panel in a minute.");
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
      setConnecting(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Meeting? You'll need a fresh self-client code to reconnect.")) return;
    try { await api.post("/admin/zoho/meeting/disconnect"); toast.success("Disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
  };

  const authorise = async (id) => {
    if (!window.confirm("Authorise this request? A live Zoho Meeting link will be generated and emailed to the partner.")) return;
    setBusy(id);
    try {
      const r = await api.post(`/admin/meeting-requests/${id}/authorise`, {});
      setLastLink(r.data.join_url);
      toast.success("Authorised · link emailed to partner");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Authorise failed");
    } finally { setBusy(""); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason (optional):", "");
    if (reason === null) return;
    setBusy(id);
    try { await api.post(`/admin/meeting-requests/${id}/reject`, { reason: reason.trim() }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(""); }
  };
  const quickStart = async () => {
    if (!qs.partner_email || !qs.topic) { toast.error("Partner email + topic required"); return; }
    setQsBusy(true);
    try {
      const r = await api.post("/admin/virtual-meetings/quick-start", qs);
      setLastLink(r.data.join_url);
      toast.success("Meeting created · link emailed");
      setQs({ partner_email: "", topic: "", agenda: "", duration_mins: 30 });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Quick-start failed");
    } finally { setQsBusy(false); }
  };

  const connected = !!status?.connected;
  const zohoState = status?.state || "idle";

  return (
    <div className="pt-2" data-testid="admin-virtual-meetings">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#0e0e0e] border border-[#D8B23E]/40 grid place-items-center">
            <Video size={18} className="text-[#D8B23E]" />
          </div>
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">Virtual Meetings</h2>
            <p className="font-body text-xs text-[#8A8A82] mt-1">Zoho Meeting requests · authorised by the admin.</p>
          </div>
        </div>
        <span className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full border ${connected ? "text-[#2FB999] border-[#2FB999]/40" : zohoState === "connecting" ? "text-[#D8B23E] border-[#D8B23E]/40" : zohoState === "failed" ? "text-[#C0392B] border-[#C0392B]/40" : "text-[#8A8A82] border-white/20"}`}>
          {connected ? "Zoho Meeting · Live" : zohoState === "connecting" ? "Connecting…" : zohoState === "failed" ? "Last Attempt Failed" : "Not Connected"}
        </span>
      </div>

      {/* Connection panel */}
      {!connected && (
        <div className="border border-white/10 rounded-sm p-5 mb-8 bg-black/30">
          <div className="flex items-center gap-2 mb-3">
            <Settings size={14} className="text-[#D8B23E]" />
            <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Connect Zoho Meeting</span>
          </div>
          {status?.last_error && zohoState === "failed" && (
            <div className="mb-3 p-3 rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/10 text-[12px] text-[#C0392B]" data-testid="zoho-connect-last-error">
              <b>Last attempt failed · {status.last_error.code}</b> — {status.last_error.message}
            </div>
          )}
          {zohoState === "connecting" && (
            <div className="mb-3 p-3 rounded-sm border border-[#D8B23E]/40 bg-[#D8B23E]/5 text-[12px] text-[#D8B23E] flex items-center gap-2" data-testid="zoho-connecting-banner">
              <Loader2 size={13} className="animate-spin" />
              Connecting to Zoho in the background… we'll refresh this panel automatically when done.
            </div>
          )}
          <p className="font-body text-[13px] text-[#8A8A82] leading-relaxed mb-4">
            Go to <a className="text-[#D8B23E] underline" href="https://api-console.zoho.in/" target="_blank" rel="noreferrer">api-console.zoho.in</a>{" "}
            → open the existing Vinci Self Client → <b>Generate Code</b> with scope{" "}
            <code className="text-[#D8B23E]">ZohoMeeting.meeting.CREATE</code>. Paste the one-time code below.
          </p>
          <div className="flex flex-wrap gap-3">
            <input
              data-testid="zoho-meeting-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="1000.xxxxxxxxxxxxxxxxxx"
              disabled={connecting || zohoState === "connecting"}
              className="flex-1 min-w-[280px] bg-transparent border border-white/15 rounded-sm px-3 py-2 font-mono text-sm outline-none focus:border-[#D8B23E] disabled:opacity-50"
            />
            <button
              data-testid="zoho-meeting-connect"
              onClick={connect}
              disabled={connecting || zohoState === "connecting" || !code.trim()}
              className="inline-flex items-center gap-2 rounded-sm bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-5 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40"
            >
              {(connecting || zohoState === "connecting") ? <Loader2 size={13} className="animate-spin"/> : <LinkIcon size={13}/>}
              {zohoState === "connecting" ? "Connecting…" : "Connect"}
            </button>
          </div>
        </div>
      )}
      {connected && (
        <div className="flex flex-wrap items-center gap-3 mb-6 text-[#8A8A82] text-[11px]">
          <span>Connected: <span className="font-mono text-[#D8B23E]">zsoid {status?.zsoid}</span></span>
          <span>·</span>
          <span>Presenter: <span className="font-mono text-[#D8B23E]">{status?.presenter_email || "—"}</span></span>
          <button onClick={disconnect} className="ml-auto text-[#C0392B] hover:opacity-80 font-grotesk uppercase tracking-[0.22em]">Disconnect</button>
        </div>
      )}

      {/* Quick Start */}
      <div className="border border-[#D8B23E]/20 rounded-sm p-5 mb-8" data-testid="quick-start-panel">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-[#D8B23E]" />
          <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Quick-Start a Meeting</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="qs-email"    value={qs.partner_email} onChange={(e) => setQs({ ...qs, partner_email: e.target.value })} placeholder="partner@email.com" className="bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"/>
          <input data-testid="qs-topic"    value={qs.topic}         onChange={(e) => setQs({ ...qs, topic: e.target.value })}         placeholder="Topic" className="bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"/>
          <input data-testid="qs-agenda"   value={qs.agenda}        onChange={(e) => setQs({ ...qs, agenda: e.target.value })}        placeholder="Agenda (optional)" className="md:col-span-2 bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"/>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 font-body text-[12px] text-[#8A8A82]">
            Duration
            <input data-testid="qs-duration" type="number" min={15} max={240} step={15} value={qs.duration_mins}
                   onChange={(e) => setQs({ ...qs, duration_mins: Number(e.target.value) })}
                   className="w-20 bg-transparent border border-white/15 rounded-sm px-2 py-1 font-body text-sm outline-none focus:border-[#D8B23E]"/>
            <span>min</span>
          </label>
          <button
            data-testid="qs-start"
            onClick={quickStart}
            disabled={qsBusy || !connected || !qs.partner_email || !qs.topic}
            className="ml-auto inline-flex items-center gap-2 rounded-sm bg-[#D8B23E] text-black px-5 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40"
          >
            {qsBusy ? <Loader2 size={13} className="animate-spin"/> : <Video size={13}/>} Start Now
          </button>
        </div>
        {lastLink && (
          <div className="mt-4 p-3 rounded-sm bg-black/40 border border-[#D8B23E]/25 flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] text-[#D8B23E] truncate">{lastLink}</span>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Copied"); }} className="text-[#8A8A82] hover:text-[#D8B23E]"><Copy size={13}/></button>
              <a href={lastLink} target="_blank" rel="noreferrer" className="text-[#8A8A82] hover:text-[#D8B23E]"><ExternalLink size={13}/></a>
            </div>
          </div>
        )}
      </div>

      {/* Request queue */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {["pending", "authorised", "rejected"].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
                    data-testid={`vm-filter-${s}`}
                    className={`rounded-full px-3.5 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] border ${filter === s ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#C9C9C2] hover:border-[#D8B23E]"}`}>{s}</button>
          ))}
        </div>
        <button onClick={load} className="text-[#8A8A82] hover:text-[#B87333]"><RefreshCw size={14}/></button>
      </div>
      {rows.length === 0 ? (
        <div className="border border-white/10 rounded-sm p-8 text-center font-body text-sm text-[#8A8A82]">No {filter} requests.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                          className="border border-white/10 rounded-sm p-5 hover:border-[#D8B23E]/40 transition-colors"
                          data-testid={`vm-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-serif text-lg truncate">{r.topic}</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-0.5">
                    {r.business_name || r.requester_email || "—"} · {r.requester_email} · Submitted {fmtWhen(r.submitted_at)}
                  </div>
                  {r.agenda && <div className="mt-2 border-l-2 border-[#D8B23E] pl-3 py-1 text-[13px] text-[#C9C9C2] italic">"{r.agenda}"</div>}
                  {r.preferred_start && <div className="mt-2 text-[11px] text-[#8A8A82]">Preferred: {fmtWhen(r.preferred_start)}</div>}
                  {r.join_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <a href={r.join_url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-[#D8B23E] hover:underline break-all">{r.join_url}</a>
                      <button onClick={() => { navigator.clipboard.writeText(r.join_url); toast.success("Copied"); }} className="text-[#8A8A82] hover:text-[#D8B23E]"><Copy size={12}/></button>
                    </div>
                  )}
                </div>
                {r.status === "pending" ? (
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`vm-authorise-${r.id}`} disabled={busy === r.id || !connected} onClick={() => authorise(r.id)}
                            className="inline-flex items-center gap-2 rounded-full bg-[#2FB999] text-black px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:opacity-90 disabled:opacity-40">
                      {busy === r.id ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Authorise
                    </button>
                    <button data-testid={`vm-reject-${r.id}`} disabled={busy === r.id} onClick={() => reject(r.id)}
                            className="inline-flex items-center gap-2 rounded-full border border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-40">
                      <XCircle size={12}/> Reject
                    </button>
                  </div>
                ) : (
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border shrink-0"
                          style={{ color: r.status === "authorised" ? "#2FB999" : "#C0392B",
                                     borderColor: (r.status === "authorised" ? "#2FB999" : "#C0392B") + "55" }}>
                    {r.status}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
